/**
 * Client-side gate deciding whether an inbound message is worth asking the backend
 * to suggest a reply for. It suppresses ~60% of requests (non-questions), so we never
 * call the endpoint for "ok thanks" etc.
 *
 * This MUST stay in sync with the backend gate (the source of truth), in
 * reffie-backend `reffie/services/nudges/reply_suggestions/detection.py`.
 */

const GATE_MAX_LEN = 300;

// Anchored, because an interrogative mid-sentence is usually not a question ("that's what
// I needed"). A question does often carry a conversational prefix though -- "Ok who is
// this", "Yes, so what is the rent" -- so a few of those are allowed ahead of it.
const PREFIX =
    '(?:(?:ok(?:ay)?|yes|yeah|no|hi|hello|hey|thanks|thank you|and|so|but|sorry|also|well|um)[\\s,.!;:-]+){0,3}';
const STARTERS = new RegExp(
    `^["'“‘\\s]*${PREFIX}(what|how|which|where|who\\b|why\\b|(is|are|when|can|could|do|does|did|would)\\s|i['’]d like to|i would like to)`,
    'i'
);

// Two groups: phrasings that make a message a question at all, then the subjects we can
// answer. A subject alone is enough to pass — the classifier, not this gate, decides what
// the message actually asks.
const CONTAINS =
    /interested in|\btour|\bbed(room)?s?\b|\dbr\b|\d br\b|studio|availab|address|located|how much|send (me|us|it|the|a |an )|please send|(want|like) to (see|view|visit|come)|\bis there\b|\bare there\b|\bdo you\b|\bdoes it\b|(need|want|wanted|like) to know|was wondering|let me know|\brent\b|\bprice[sd]?\b|\bparking\b|\bgarage\b|\bvoucher|section ?8\b|housing assistance|low[- ]income|income[- ](restricted|limit|cap)|affordable housing|your name\b|\bwho is this\b|\bwho'?s this\b|\bwho am i\b/i;

// A tapback ("Loved “…”", "👍 to “…”") arrives as an inbound message quoting our own
// outbound text, so it inherits any question mark *we* wrote. Left alone it looks like the
// renter asking our own question back at us. A reaction is never a question.
const REACTION =
    /^\s*(?:(?:loved|liked|disliked|laughed at|emphasi[sz]ed|questioned)\s|removed\b[^"'“‘]{0,20}\bfrom\s|[^\s\w]{1,5}\s+(?:to|a|from)\s)["'“‘]/i;

export const passesReplyGate = (contents: string): boolean =>
    contents.length <= GATE_MAX_LEN &&
    !REACTION.test(contents) &&
    (contents.includes('?') || STARTERS.test(contents) || CONTAINS.test(contents));
